/**
 * Date formatting utilities with locale-specific support
 *
 * This module handles conversion between:
 * - Storage format: ISO YYYY-MM-DD (database format)
 * - Display format: Locale-specific (DD-MM-YYYY for France, MM-DD-YYYY for US, etc.)
 */

export type CountryCode = 'US' | 'FR' | 'ES' | 'IT' | string;

export interface DateFormat {
  pattern: string; // Internal pattern for logic (e.g., "DD-MM-YYYY")
  localizedPattern: string; // Localized pattern for display (e.g., "JJ-MM-AAAA" for French)
  placeholder: string;
  separator: string;
  dayPosition: number;
  monthPosition: number;
  yearPosition: number;
}

/**
 * Date format configurations by country code
 * Pattern uses: D = day, M = month, Y = year
 */
const DATE_FORMATS: Record<string, DateFormat> = {
  // United States - MM-DD-YYYY
  'US': {
    pattern: 'MM-DD-YYYY',
    localizedPattern: 'MM-DD-YYYY',
    placeholder: '12-31-1990',
    separator: '-',
    dayPosition: 1,
    monthPosition: 0,
    yearPosition: 2,
  },
  // France - DD-MM-YYYY (displayed as JJ-MM-AAAA)
  'FR': {
    pattern: 'DD-MM-YYYY',
    localizedPattern: 'JJ-MM-AAAA',
    placeholder: '31-12-1990',
    separator: '-',
    dayPosition: 0,
    monthPosition: 1,
    yearPosition: 2,
  },
  // Spain - DD-MM-YYYY (displayed as DD-MM-AAAA)
  'ES': {
    pattern: 'DD-MM-YYYY',
    localizedPattern: 'DD-MM-AAAA',
    placeholder: '31-12-1990',
    separator: '-',
    dayPosition: 0,
    monthPosition: 1,
    yearPosition: 2,
  },
  // Italy - DD-MM-YYYY (displayed as GG-MM-AAAA)
  'IT': {
    pattern: 'DD-MM-YYYY',
    localizedPattern: 'GG-MM-AAAA',
    placeholder: '31-12-1990',
    separator: '-',
    dayPosition: 0,
    monthPosition: 1,
    yearPosition: 2,
  },
  // Default fallback - ISO format
  'DEFAULT': {
    pattern: 'YYYY-MM-DD',
    localizedPattern: 'YYYY-MM-DD',
    placeholder: '1990-12-31',
    separator: '-',
    dayPosition: 2,
    monthPosition: 1,
    yearPosition: 0,
  },
};

/**
 * Get the date format configuration for a country
 */
export function getDateFormatForCountry(countryCode: string | null): DateFormat {
  if (!countryCode) {
    return DATE_FORMATS.DEFAULT;
  }

  const upperCode = countryCode.toUpperCase();
  return DATE_FORMATS[upperCode] || DATE_FORMATS.DEFAULT;
}

/**
 * Convert ISO date string (YYYY-MM-DD) to locale-specific display format
 * @param isoDate - Date string in ISO format (YYYY-MM-DD)
 * @param countryCode - Country code to determine format
 * @returns Date string in locale-specific format
 */
export function formatDateForDisplay(isoDate: string, countryCode: string | null): string {
  if (!isoDate) return '';

  try {
    const format = getDateFormatForCountry(countryCode);
    const parts = isoDate.split('-');

    if (parts.length !== 3) {
      console.warn('[DateFormatting] Invalid ISO date format:', isoDate);
      return isoDate;
    }

    const [year, month, day] = parts;

    // Create array in the correct order based on format
    const displayParts = ['', '', ''];
    displayParts[format.dayPosition] = day;
    displayParts[format.monthPosition] = month;
    displayParts[format.yearPosition] = year;

    return displayParts.join(format.separator);
  } catch (error) {
    console.error('[DateFormatting] Error formatting date:', error);
    return isoDate;
  }
}

/**
 * Parse locale-specific date string to ISO format (YYYY-MM-DD)
 * @param displayDate - Date string in locale-specific format
 * @param countryCode - Country code to determine format
 * @returns Date string in ISO format (YYYY-MM-DD)
 */
export function parseDateFromDisplay(displayDate: string, countryCode: string | null): string {
  if (!displayDate) return '';

  try {
    const format = getDateFormatForCountry(countryCode);
    const parts = displayDate.split(format.separator);

    if (parts.length !== 3) {
      console.warn('[DateFormatting] Invalid display date format:', displayDate);
      return displayDate;
    }

    // Extract day, month, year from the correct positions
    const day = parts[format.dayPosition];
    const month = parts[format.monthPosition];
    const year = parts[format.yearPosition];

    // Return in ISO format: YYYY-MM-DD
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('[DateFormatting] Error parsing date:', error);
    return displayDate;
  }
}

/**
 * Validate a date string in locale-specific format
 * @param displayDate - Date string in locale-specific format
 * @param countryCode - Country code to determine format
 * @returns Object with isValid flag and error message if invalid
 */
export function validateDisplayDate(
  displayDate: string,
  countryCode: string | null
): { isValid: boolean; error?: string } {
  if (!displayDate) {
    return { isValid: false, error: 'Date is required' };
  }

  const format = getDateFormatForCountry(countryCode);
  const parts = displayDate.split(format.separator);

  if (parts.length !== 3) {
    return {
      isValid: false,
      error: `Please use the format: ${format.pattern}`,
    };
  }

  // Extract day, month, year from the correct positions
  const dayStr = parts[format.dayPosition];
  const monthStr = parts[format.monthPosition];
  const yearStr = parts[format.yearPosition];

  // Validate numeric values
  const day = parseInt(dayStr, 10);
  const month = parseInt(monthStr, 10);
  const year = parseInt(yearStr, 10);

  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    return {
      isValid: false,
      error: `Please use the format: ${format.pattern}`,
    };
  }

  // Validate ranges
  if (month < 1 || month > 12) {
    return { isValid: false, error: 'Invalid month' };
  }

  if (day < 1 || day > 31) {
    return { isValid: false, error: 'Invalid day' };
  }

  if (year < 1900 || year > new Date().getFullYear()) {
    return { isValid: false, error: 'Invalid year' };
  }

  // Validate day in month (including leap year for February)
  const daysInMonth = getDaysInMonth(month, year);
  if (day > daysInMonth) {
    return {
      isValid: false,
      error: `Invalid day for the selected month`,
    };
  }

  // Validate that date is not in the future
  const date = new Date(year, month - 1, day);
  if (date > new Date()) {
    return { isValid: false, error: 'Date cannot be in the future' };
  }

  return { isValid: true };
}

/**
 * Get the number of days in a month (accounting for leap years)
 */
function getDaysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * Format a Date object to ISO string (YYYY-MM-DD)
 * @param date - JavaScript Date object
 * @returns Date string in ISO format
 */
export function dateToISOString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a Date object to locale-specific display format
 * @param date - JavaScript Date object
 * @param countryCode - Country code to determine format
 * @returns Date string in locale-specific format
 */
export function dateToDisplayString(date: Date, countryCode: string | null): string {
  const isoString = dateToISOString(date);
  return formatDateForDisplay(isoString, countryCode);
}

/**
 * Get example date string for a given country
 * @param countryCode - Country code
 * @returns Example date string in that country's format
 */
export function getDateExample(countryCode: string | null): string {
  const format = getDateFormatForCountry(countryCode);
  return format.placeholder;
}

/**
 * Get date format pattern for a given country (internal pattern for logic)
 * @param countryCode - Country code
 * @returns Date format pattern (e.g., "DD-MM-YYYY")
 */
export function getDatePattern(countryCode: string | null): string {
  const format = getDateFormatForCountry(countryCode);
  return format.pattern;
}

/**
 * Get localized date format pattern for a given country (for display to users)
 * @param countryCode - Country code
 * @returns Localized date format pattern (e.g., "JJ-MM-AAAA" for French, "DD-MM-YYYY" for English)
 */
export function getLocalizedDatePattern(countryCode: string | null): string {
  const format = getDateFormatForCountry(countryCode);
  return format.localizedPattern;
}
